/**
 * Раздел «Сельское хозяйство» страницы «Аналитика» (US-04.5): верхняя
 * плашка из 5 показателей + виджеты в порядке FR-3.1: «Структура посевных
 * площадей» (смена источника данных: объёмы урожая по группам культур —
 * бар доли от итога + значение + %) → «Динамика поголовья» (диаграмма
 * 2023–2025 во всю ширину) → объединённый виджет «Животноводство и
 * птицеводство» (из-за отсутствия данных КХ/ТОО-ЛПХ: сводная плашка
 * поголовья + таблица «Всего / В т.ч. племенные / Доля племенных», птица —
 * итоговой строкой) → пара «Ежегодное производство мяса» + «Производство
 * молочной продукции» в одном ряду.
 *
 * Плашка вычисляется из разрезов (FR-2): «Поголовье КРС» — головы
 * КХ/ТОО + ЛПХ; «Самообеспеченность» — среднее по видам (баранина,
 * птица, яйцо, огурцы, томаты); «Хозяйств» — кол-во с/х
 * товаропроизводителей (то же число, что в виджете посевных площадей);
 * «Ежегодное производство мяса» — сумма по видам («всего» виджета мяса);
 * «Валовый сбор» — итог колонки «тыс. тонн» посевных площадей.
 * Показатели плашки видны всегда (без данных — «—», замечание ревью);
 * карточки равномерно растягиваются
 * (FR-2.7); строка/бар без данных не отображается; виджет без данных
 * не рендерится. Drill-through (FR-4) — вне текущей вёрстки: детализация
 * первичных данных появится вместе с бэком.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsAgriculture.json; остальные КАТО (по выбранному
 * в сайдбаре) — один запрос /api/public/analytics/agriculture (DP-135,
 * US-04.5): plashka → верхняя плашка, sown_areas → «Структура посевных
 * площадей», livestock_dynamics → «Динамика поголовья», livestock →
 * «Животноводство», poultry → «Птицеводство», meat_production →
 * «Ежегодное производство мяса», dairy_production → «Производство
 * молочной продукции».
 */
import { Wheat } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import analyticsAgriculture from '../../../hardData/analyticsAgriculture.json';
import { getAnalyticsAgriculture } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { AgricultureAnalyticsResponse } from '../../../types/analytics';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);
/** Доля с одним знаком, без «%» — для колонок под заголовком «%». */
const fmtShare = (v: number): string =>
  Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(v);

/** Проценты долей — всегда с одним знаком («89,0%», как на макете). */
const fmtPct = (v: number): string => `${fmtShare(v)}%`;

/** Сумма компонент; все null → null (в плашке показывается «—»). */
const sum = (...parts: Array<number | null>): number | null => {
  const known = parts.filter((v): v is number => v !== null);
  return known.length === 0 ? null : known.reduce((acc, v) => acc + v, 0);
};

const cell = (v: number | null): string => (v !== null ? fmt(v) : '—');

/** Виды «Динамики поголовья» (FR-3.3.1) — порядок по постановке. */
const DYNAMICS_SPECIES = [
  'cattle',
  'sheep',
  'horses',
  'poultry',
  'camels',
] as const;

/** Цвета баров лет динамики — оттенки chart-blue темы. */
const DYNAMICS_COLORS = ['#AAB8F0', '#8FA3F2', '#4A63E7'];

/** crop ответа API → ключ подписи sownAreas.volumeRows.*. */
const CROP_KEYS: Record<string, string> = {
  grains_legumes: 'grainAndLegumes',
  oilseeds: 'oilseeds',
  fodder: 'fodder',
  potato: 'potato',
  vegetables: 'vegetables',
};

/** animal ответа API → ключ подписи husbandry.species.*. */
const ANIMAL_KEYS: Record<string, string> = {
  cattle: 'cattle',
  small_cattle: 'sheep',
  horses: 'horses',
  birds: 'poultry',
  camels: 'camels',
  pigs: 'pigs',
};

/** product ответа API → ключ подписи dairy.products.*. */
const DAIRY_KEYS: Record<string, string> = {
  butter: 'butter',
  cottage_cheese: 'cottageCheese',
  sour_cream: 'sourCream',
  kefir: 'kefir',
  milk: 'milk',
};

interface HusbandryRowProps {
  testId: string;
  label: string;
  total: number;
  breeding: number | null;
  /** Доля племенных, % — null, когда племенных данных нет («—» + пустой трек). */
  share: number | null;
  /** Максимальная доля по таблице — бары масштабируются к ней. */
  maxShare: number;
  /** Подстрока-детализация («в том числе коровы») — приглушённая, без доли. */
  subRow: { label: string; total: number } | null;
}

/** Строка таблицы «Животноводство и птицеводство»: вид · всего ·
 * в т.ч. племенные · мини-бар доли племенных. Вид с нулевым поголовьем —
 * приглушённый (макет: «Верблюды 0 — — —»). */
const HusbandryRow = ({
  testId,
  label,
  total,
  breeding,
  share,
  maxShare,
  subRow,
}: HusbandryRowProps) => {
  const muted = total === 0;
  return (
    <>
      <li
        data-testid={testId}
        className={`flex items-center gap-3 border-b border-surface-border px-3 py-3.5 text-sm last:border-b-0 last:pb-0${muted ? ' text-text-faint' : ''}`}
      >
        <span className="min-w-24 flex-1 font-semibold leading-snug">
          {label}
        </span>
        <span className="w-24 shrink-0 text-right font-extrabold tabular-nums">
          {fmt(total)}
        </span>
        <span className="w-24 shrink-0 text-right font-bold tabular-nums">
          {breeding !== null ? fmt(breeding) : '—'}
        </span>
        <span className="flex w-36 shrink-0 items-center justify-end gap-2">
          <span className="h-4 w-16 shrink-0 overflow-hidden rounded-full bg-surface-muted">
            {share !== null && (
              <span
                className="block h-full rounded-full bg-chart-blue"
                style={{ width: `${Math.max(4, (share / maxShare) * 100)}%` }}
              />
            )}
          </span>
          <span className="w-12 shrink-0 text-right font-bold tabular-nums text-text-faint">
            {share !== null ? fmtPct(share) : '—'}
          </span>
        </span>
      </li>
      {subRow && (
        <li
          data-testid={`${testId}-cows`}
          className="flex items-center gap-3 border-b border-surface-border px-3 py-3.5 text-sm text-text-muted last:border-b-0 last:pb-0"
        >
          <span className="min-w-24 flex-1 font-semibold leading-snug">
            –&ensp;{subRow.label}
          </span>
          <span className="w-24 shrink-0 text-right font-bold tabular-nums">
            {fmt(subRow.total)}
          </span>
          <span className="w-24 shrink-0 text-right font-bold tabular-nums">
            —
          </span>
          <span className="flex w-36 shrink-0 items-center justify-end gap-2">
            <span className="h-4 w-16 shrink-0 overflow-hidden rounded-full bg-surface-muted" />
            <span className="w-12 shrink-0 text-right font-bold tabular-nums text-text-faint">
              —
            </span>
          </span>
        </li>
      )}
    </>
  );
};

interface AgricultureSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты — «Структура
   * посевных площадей» и блок животноводства/птицеводства (FR-2.2). */
  summary?: boolean;
}

export const AgricultureSection = ({
  summary = false,
}: AgricultureSectionProps = {}) => {
  const { t } = useTranslation();

  const { data, loading, isHardKato } =
    useAnalyticsSection<AgricultureAnalyticsResponse>(
      'agriculture',
      getAnalyticsAgriculture,
    );

  // --- Разрезы в общей форме: мок для демо-КАТО,
  // /analytics/agriculture для остальных ---

  const cropStructure: Array<{
    key: string;
    area: number | null;
    gross: number | null;
  }> = isHardKato
    ? analyticsAgriculture.cropStructure
    : (data?.sown_areas ?? []).map((row) => ({
        key: CROP_KEYS[row.crop] ?? row.crop,
        area: row.area_ths_ha,
        gross: row.gross_harvest_ths_t,
      }));

  const livestockDynamics: Array<
    { year: number } & Record<(typeof DYNAMICS_SPECIES)[number], number | null>
  > = isHardKato
    ? analyticsAgriculture.livestockDynamics
    : (data?.livestock_dynamics ?? [])
        // Последние три года — по числу цветов легенды (как на макете).
        .slice(-DYNAMICS_COLORS.length)
        .map((row) => ({
          year: row.year,
          cattle: row.cattle,
          sheep: row.small_cattle,
          horses: row.horses,
          poultry: row.birds,
          camels: row.camels,
        }));

  // «Всего» — вычисляемое (КХ/ТОО + ЛПХ); у API есть готовый total.
  // breeding/cows — «Мониторинг СНП» (DP-267), в демо-моке их нет.
  const livestock: Array<{
    key: string;
    khToo: number | null;
    lph: number | null;
    total: number | null;
    meatDirection: number | null;
    breeding: number | null;
    cows: number | null;
  }> = isHardKato
    ? analyticsAgriculture.livestock.map((row) => ({
        key: row.key,
        khToo: row.khToo,
        lph: row.lph,
        total: sum(row.khToo, row.lph),
        meatDirection: row.meatDirection,
        breeding: null,
        cows: null,
      }))
    : (data?.livestock ?? []).map((row) => ({
        key: ANIMAL_KEYS[row.animal] ?? row.animal,
        khToo: row.kh_too,
        lph: row.lph,
        total: row.total ?? sum(row.kh_too, row.lph),
        meatDirection: row.meat_direction,
        breeding: row.breeding,
        cows: row.cows,
      }));

  const poultry: Array<{
    key: string;
    khToo: number | null;
    lph: number | null;
    total: number | null;
  }> = isHardKato
    ? analyticsAgriculture.poultry.map((row) => ({
        key: row.key,
        khToo: row.khToo,
        lph: row.lph,
        total: sum(row.khToo, row.lph),
      }))
    : (data?.poultry ?? []).map((row) => ({
        key: row.kind,
        khToo: row.kh_too,
        lph: row.lph,
        total: row.total ?? sum(row.kh_too, row.lph),
      }));

  const meatProduction: Array<{ key: string; tons: number | null }> = isHardKato
    ? analyticsAgriculture.meatProduction
    : (data?.meat_production ?? []).map((row) => ({
        key: ANIMAL_KEYS[row.animal] ?? row.animal,
        tons: row.tons,
      }));

  const dairyProduction: Array<{
    key: string;
    tons: number | null;
    milkUsage: number | null;
  }> = isHardKato
    ? analyticsAgriculture.dairyProduction
    : (data?.dairy_production ?? []).map((row) => ({
        key: DAIRY_KEYS[row.product] ?? row.product,
        tons: row.annual_output_t,
        milkUsage: row.milk_consumption_ths_l,
      }));

  const farmProducers = isHardKato
    ? analyticsAgriculture.farmProducers
    : (data?.plashka.producers ?? null);

  // --- Плашка (FR-2): демо-КАТО — вычисляется из разрезов мока,
  // остальные — plashka ответа (с фолбэком на суммы разрезов) ---

  const cattleRow = livestock.find((row) => row.key === 'cattle');
  const cattleTotal = isHardKato
    ? (cattleRow?.total ?? null)
    : (data?.plashka.cattle_heads ?? cattleRow?.total ?? null);

  const selfAvg = isHardKato
    ? (() => {
        const selfValues = Object.values(
          analyticsAgriculture.selfSufficiency,
        ).filter((v): v is number => v !== null);
        return selfValues.length > 0
          ? selfValues.reduce((acc, v) => acc + v, 0) / selfValues.length
          : null;
      })()
    : (data?.plashka.self_sufficiency_pct ?? null);

  const meatSum = sum(...meatProduction.map((row) => row.tons));
  const meatTotal = isHardKato
    ? meatSum
    : (data?.plashka.meat_total_t ?? meatSum);
  const grossTotal = isHardKato
    ? sum(...cropStructure.map((row) => row.gross))
    : (data?.plashka.gross_harvest_ths_t ??
      sum(...cropStructure.map((row) => row.gross)));

  // Чипы динамики плашки — только в демо-режиме (в API их нет).
  const trends: Record<string, string | undefined> = isHardKato
    ? analyticsAgriculture.cardTrends
    : {};
  // Плашка всегда показывает все показатели; нет данных → «—»
  // (замечание дизайн-ревью).
  const cards: Array<{ key: string; value: string; unit: string | null }> = [
    {
      key: 'cattleStock',
      value: cattleTotal !== null ? fmt(cattleTotal) : '—',
      unit: t('units.pieces'),
    },
    {
      key: 'selfSufficiency',
      value: selfAvg !== null ? `${fmt1(selfAvg)}%` : '—',
      unit: null,
    },
    {
      key: 'farms',
      value: farmProducers !== null ? fmt(farmProducers) : '—',
      unit: t('units.pieces'),
    },
    {
      key: 'meatProduction',
      value: meatTotal !== null ? fmt(meatTotal) : '—',
      unit: t('analytics.agriculture.unitTons'),
    },
    {
      key: 'grossHarvest',
      value: grossTotal !== null ? fmt(grossTotal) : '—',
      unit: t('analytics.agriculture.unitThousandTons'),
    },
  ];

  // --- «Структура посевных площадей» (FR-3.2, смена источника данных):
  // по каждой группе культур — объём урожая, бар и доля от итога;
  // культура без данных не отображается. Итог — первой строкой
  // («Итоговый объем урожая», 100%), как на макете ---
  // Итог: бэк отдаёт готовый sown_total (DP-135) — суммирование
  // на фронте остаётся фолбэком (демо-мок и старые ответы).
  const cropVolumeTotal =
    (!isHardKato ? data?.sown_total?.gross_harvest_ths_t : null) ??
    sum(...cropStructure.map((row) => row.gross));
  const cropRows = cropStructure.flatMap((row) => {
    if (row.gross === null) return [];
    const pct =
      cropVolumeTotal !== null && cropVolumeTotal > 0
        ? (row.gross / cropVolumeTotal) * 100
        : null;
    return [
      {
        key: row.key,
        value: fmt(row.gross),
        pct: pct !== null ? fmtShare(pct) : '—',
        barPercent: pct !== null ? Math.max(1, pct) : 0,
      },
    ];
  });

  // --- «Животноводство и птицеводство» (FR-3.3) ---

  const dynamicsYears = livestockDynamics.map((point) => point.year);
  const dynamicsData = DYNAMICS_SPECIES.map((species) => ({
    species: t(`analytics.agriculture.husbandry.species.${species}`),
    ...Object.fromEntries(
      livestockDynamics.map((point) => [`y${point.year}`, point[species]]),
    ),
  }));
  const dynamicsConfig: ChartConfig = Object.fromEntries(
    livestockDynamics.map((point, index) => [
      `y${point.year}`,
      { label: String(point.year), color: DYNAMICS_COLORS[index] },
    ]),
  );

  // Объединённый виджет «Животноводство и птицеводство» (смена источника
  // данных: разрез КХ/ТОО-ЛПХ и мясное направление недоступны).
  // Доля племенных считается от «всего» вида; вид без данных не
  // отображается; вид с нулевым поголовьем — приглушённой строкой.
  const husbandryShare = (
    breeding: number | null,
    total: number | null,
  ): number | null =>
    breeding !== null && total !== null && total > 0
      ? (breeding / total) * 100
      : null;

  const husbandryAnimals = livestock.flatMap((row) => {
    if (row.total === null) return [];
    return [
      {
        key: row.key,
        total: row.total,
        breeding: row.breeding,
        cows: row.cows,
        share: husbandryShare(row.breeding, row.total),
      },
    ];
  });
  // Птица — итоговой строкой таблицы. API отдаёт готовый poultry_total
  // (DP-135; разбивка poultry[] обычно пустая — источника нет); сумма
  // разбивки — фолбэк для демо-мока и старых ответов. null — «—»,
  // 0 — честный ноль.
  const poultryTotal = isHardKato
    ? sum(...poultry.map((row) => row.total))
    : (data?.poultry_total ?? sum(...poultry.map((row) => row.total)));

  // Бары «Доля племенных» масштабируются к максимальной доле (как бары
  // «Ежегодного производства мяса» — к максимуму).
  const maxShare = Math.max(
    1,
    ...husbandryAnimals.map((row) => row.share ?? 0),
  );

  // Сводная плашка виджета: поголовье с/х животных (сумма видов, коровы —
  // подмножество КРС и не суммируются), птица, доля племенного поголовья.
  const husbandryLivestockTotal = sum(
    ...husbandryAnimals.map((row) => row.total),
  );
  const husbandryBreedingTotal = sum(
    ...husbandryAnimals.map((row) => row.breeding),
  );
  const husbandryBreedingShare = husbandryShare(
    husbandryBreedingTotal,
    husbandryLivestockTotal,
  );

  // --- «Ежегодное производство мяса» (FR-3.4): бары — доля от максимума ---
  const meatKnown = meatProduction.filter(
    (row): row is { key: string; tons: number } => row.tons !== null,
  );
  const meatMax = Math.max(1, ...meatKnown.map((row) => row.tons));
  const meatRows = meatKnown.map((row) => ({
    key: row.key,
    value: fmt(row.tons),
    barPercent: Math.max(2, (row.tons / meatMax) * 100),
  }));

  // --- «Производство молочной продукции» (FR-3.5) ---
  const dairyRows = dairyProduction.flatMap((row) =>
    row.tons === null && row.milkUsage === null
      ? []
      : [
          {
            key: row.key,
            tons: cell(row.tons),
            milkUsage: cell(row.milkUsage),
          },
        ],
  );

  return (
    <section className="space-y-4" data-testid="analytics-agriculture">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.agriculture.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Wheat
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>
          {/* Плашка: все показатели всегда видны, без данных — «—». */}
          {!summary && (
            <div className="flex flex-wrap gap-3.5">
              {cards.map((card) => (
                <div key={card.key} className="min-w-52 flex-1">
                  <StatCard
                    testId={`analytics-card-${card.key}`}
                    title={t(`analytics.agriculture.cards.${card.key}`)}
                    value={card.value}
                    unit={card.unit}
                    trend={
                      trends[card.key] != null
                        ? { text: trends[card.key] as string }
                        : null
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {/* Виджеты всегда видны: без данных — заголовок + «Нет данных»
          (замечание дизайн-ревью). */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>
                {t('analytics.agriculture.sownAreas.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cropRows.length === 0 ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <div data-testid="analytics-sown-areas">
                  {/* Единицы измерения — над столбцами: «тонн» / «%» / «ед.»
                  (замечание ревью); «ед.» — колонка строки кол-ва
                  с/х товаропроизводителей. */}
                  <div className="flex items-center gap-4 px-3 pb-2 text-sm leading-tight text-text-faint">
                    <span className="min-w-0 flex-1 sm:flex-none sm:basis-1/3" />
                    <span className="hidden min-w-0 flex-1 sm:block" />
                    <span className="w-24 shrink-0 text-right">
                      {t('analytics.agriculture.unitTons')}
                    </span>
                    <span className="w-14 shrink-0 text-right">%</span>
                    <span className="w-16 shrink-0 text-right">
                      {t('units.pieces')}
                    </span>
                  </div>
                  {/* Итог — первой строкой с полным баром и 100% (макет). */}
                  {cropVolumeTotal !== null && (
                    <div
                      className="flex items-center gap-4 rounded-lg bg-surface-muted/60 p-3 text-sm font-extrabold"
                      data-testid="analytics-sown-areas-total"
                    >
                      <span className="min-w-0 flex-1 leading-snug sm:flex-none sm:basis-1/3">
                        {t('analytics.agriculture.sownAreas.totalRow')}
                      </span>
                      <span className="hidden h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted sm:block">
                        <span className="block h-full w-full rounded-full bg-chart-blue" />
                      </span>
                      <span className="w-24 shrink-0 text-right tabular-nums">
                        {fmt(cropVolumeTotal)}
                      </span>
                      <span className="w-14 shrink-0 text-right font-bold tabular-nums text-text-faint">
                        100
                      </span>
                      <span className="w-16 shrink-0" />
                    </div>
                  )}
                  <ul>
                    {cropRows.map((row) => (
                      <li
                        key={row.key}
                        data-testid={`analytics-sown-areas-${row.key}`}
                        className="flex items-center gap-4 border-b border-surface-border px-3 py-3.5 text-sm last:border-b-0 last:pb-0"
                      >
                        <span className="min-w-0 flex-1 font-semibold leading-snug sm:flex-none sm:basis-1/3">
                          {t(
                            `analytics.agriculture.sownAreas.volumeRows.${row.key}`,
                          )}
                        </span>
                        <span className="hidden h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted sm:block">
                          <span
                            className="block h-full rounded-full bg-chart-blue"
                            style={{ width: `${row.barPercent}%` }}
                          />
                        </span>
                        <span className="w-24 shrink-0 text-right font-extrabold tabular-nums">
                          {row.value}
                        </span>
                        <span className="w-14 shrink-0 text-right font-bold tabular-nums text-text-faint">
                          {row.pct}
                        </span>
                        <span className="w-16 shrink-0" />
                      </li>
                    ))}
                    {farmProducers !== null && (
                      <li
                        data-testid="analytics-sown-areas-producers"
                        className="flex items-center gap-4 border-t border-surface-border px-3 py-3.5 pb-0 text-sm"
                      >
                        <span className="min-w-0 flex-1 font-semibold leading-snug">
                          {t('analytics.agriculture.sownAreas.producersRow')}
                        </span>
                        <span className="w-24 shrink-0" />
                        <span className="w-14 shrink-0" />
                        <span className="w-16 shrink-0 text-right font-extrabold tabular-nums">
                          {fmt(farmProducers)}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-card" data-testid="analytics-agri-dynamics">
            <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
              <CardTitle>
                {t('analytics.agriculture.husbandry.dynamicsTitle')}
              </CardTitle>
              {dynamicsYears.length > 0 && (
                <div className="flex items-center gap-4 text-sm text-text-muted">
                  {dynamicsYears.map((year, index) => (
                    <span key={year} className="flex items-center gap-1.5">
                      <span
                        className="size-2 shrink-0 rounded-sm"
                        style={{
                          backgroundColor: DYNAMICS_COLORS[index],
                        }}
                        aria-hidden="true"
                      />
                      {year}
                    </span>
                  ))}
                  <span className="text-text-faint">
                    {t('analytics.agriculture.unitHeads')}
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {dynamicsYears.length === 0 ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <ChartContainer config={dynamicsConfig} className="h-64 w-full">
                  <BarChart
                    data={dynamicsData}
                    margin={{ top: 8, right: 16, bottom: 0, left: 16 }}
                  >
                    <CartesianGrid vertical={false} stroke="#E4E9E5" />
                    <XAxis
                      dataKey="species"
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                    />
                    <ChartTooltip
                      cursor={false}
                      content={<ChartTooltipContent />}
                    />
                    {dynamicsYears.map((year, index) => (
                      <Bar
                        key={year}
                        dataKey={`y${year}`}
                        fill={DYNAMICS_COLORS[index]}
                        radius={4}
                        maxBarSize={32}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-card" data-testid="analytics-agri-livestock">
            <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
              <CardTitle>
                {t('analytics.agriculture.husbandry.combinedTitle')}
              </CardTitle>
              <span className="shrink-0 text-sm text-text-faint">
                {t('analytics.agriculture.unitHeads')}
              </span>
            </CardHeader>
            <CardContent>
              {husbandryAnimals.length === 0 && poultryTotal === null ? (
                <p
                  data-testid="block-placeholder"
                  className="text-sm lowercase text-text-muted"
                >
                  {t('common.noData')}
                </p>
              ) : (
                <>
                  {/* Сводная плашка поголовья */}
                  <div
                    className="mb-4 grid gap-x-6 gap-y-3 rounded-lg bg-surface-muted/60 p-4 sm:grid-cols-3"
                    data-testid="analytics-agri-husbandry-summary"
                  >
                    {(
                      [
                        ['livestock', cell(husbandryLivestockTotal)],
                        ['poultry', cell(poultryTotal)],
                        [
                          'breedingShare',
                          husbandryBreedingShare !== null
                            ? fmtPct(husbandryBreedingShare)
                            : '—',
                        ],
                      ] as const
                    ).map(([key, value]) => (
                      <div key={key} className="space-y-1">
                        <div className="text-sm leading-tight text-text-muted">
                          {t(`analytics.agriculture.husbandry.summary.${key}`)}
                        </div>
                        <div className="text-2xl font-extrabold tabular-nums leading-none tracking-tight">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="overflow-x-auto">
                    <div className="min-w-fit">
                      <div className="flex items-center gap-3 px-3 pb-2 text-sm leading-tight text-text-faint">
                        <span className="min-w-24 flex-1">
                          {t('analytics.agriculture.husbandry.columnKind')}
                        </span>
                        <span className="w-24 shrink-0 text-right">
                          {t('analytics.agriculture.husbandry.columnTotal')}
                        </span>
                        <span className="w-24 shrink-0 text-right">
                          {t('analytics.agriculture.husbandry.columnBreeding')}
                        </span>
                        <span className="w-36 shrink-0 text-right">
                          {t(
                            'analytics.agriculture.husbandry.columnBreedingShare',
                          )}
                        </span>
                      </div>
                      <ul>
                        {husbandryAnimals.map((row) => (
                          <HusbandryRow
                            key={row.key}
                            testId={`analytics-agri-livestock-${row.key}`}
                            label={t(
                              `analytics.agriculture.husbandry.table.${row.key}`,
                            )}
                            total={row.total}
                            breeding={row.breeding}
                            share={row.share}
                            maxShare={maxShare}
                            subRow={
                              // «в том числе коровы» — приглушённой строкой под КРС
                              // (племенные коровы в контракте DP-267 не приходят).
                              row.cows !== null
                                ? {
                                    label: t(
                                      'analytics.agriculture.husbandry.table.cows',
                                    ),
                                    total: row.cows,
                                  }
                                : null
                            }
                          />
                        ))}
                        {poultryTotal !== null && (
                          <HusbandryRow
                            testId="analytics-agri-livestock-poultryAll"
                            label={t(
                              'analytics.agriculture.husbandry.table.poultryAll',
                            )}
                            total={poultryTotal}
                            breeding={null}
                            share={null}
                            maxShare={maxShare}
                            subRow={null}
                          />
                        )}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {!summary && (
            <div className="grid gap-3.5 xl:grid-cols-2">
              <Card className="shadow-card">
                <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
                  <CardTitle>{t('analytics.agriculture.meat.title')}</CardTitle>
                  <span className="shrink-0 text-sm text-text-faint">
                    {t('analytics.agriculture.unitTons')}
                  </span>
                </CardHeader>
                <CardContent>
                  {meatRows.length === 0 ? (
                    <p
                      data-testid="block-placeholder"
                      className="text-sm lowercase text-text-muted"
                    >
                      {t('common.noData')}
                    </p>
                  ) : (
                    <div data-testid="analytics-agri-meat">
                      {/* «ИТОГО» — первой строкой, в едином стиле с таблицами
                      «Соц. сферы» (замечание дизайн-ревью): строки с px-3,
                      итог с p-3 — правые края значений на одной линии. */}
                      {meatTotal !== null && (
                        <div
                          className="flex items-center gap-4 rounded-lg bg-surface-muted/60 p-3 text-sm font-extrabold"
                          data-testid="analytics-agri-meat-total"
                        >
                          <span className="min-w-0 flex-1 uppercase">
                            {t('analytics.agriculture.meat.totalLabel')}
                          </span>
                          <span className="w-24 shrink-0 text-right tabular-nums">
                            {fmt(meatTotal)}
                          </span>
                        </div>
                      )}
                      <ul>
                        {meatRows.map((row) => (
                          <li
                            key={row.key}
                            data-testid={`analytics-agri-meat-${row.key}`}
                            className="flex items-center gap-4 border-b border-surface-border px-3 py-3.5 text-sm last:border-b-0 last:pb-0"
                          >
                            <span className="min-w-0 flex-1 font-semibold leading-snug sm:flex-none sm:basis-1/4">
                              {t(
                                `analytics.agriculture.husbandry.species.${row.key}`,
                              )}
                            </span>
                            <span className="hidden h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted sm:block">
                              <span
                                className="block h-full rounded-full bg-chart-blue"
                                style={{ width: `${row.barPercent}%` }}
                              />
                            </span>
                            <span className="w-24 shrink-0 text-right font-extrabold tabular-nums">
                              {row.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle>
                    {t('analytics.agriculture.dairy.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dairyRows.length === 0 ? (
                    <p
                      data-testid="block-placeholder"
                      className="text-sm lowercase text-text-muted"
                    >
                      {t('common.noData')}
                    </p>
                  ) : (
                    <div data-testid="analytics-agri-dairy">
                      <div className="flex items-center gap-3 px-3 pb-2 text-sm leading-tight text-text-faint">
                        <span className="min-w-0 flex-1" />
                        <span className="w-40 shrink-0 text-right">
                          {t('analytics.agriculture.dairy.columnOutput')}
                        </span>
                        <span className="w-44 shrink-0 text-right">
                          {t('analytics.agriculture.dairy.columnMilkUsage')}
                        </span>
                      </div>
                      <ul>
                        {dairyRows.map((row) => (
                          <li
                            key={row.key}
                            data-testid={`analytics-agri-dairy-${row.key}`}
                            className="flex items-center gap-3 border-b border-surface-border px-3 py-4 text-sm last:border-b-0 last:pb-0"
                          >
                            <span className="min-w-0 flex-1 font-semibold leading-snug">
                              {t(
                                `analytics.agriculture.dairy.products.${row.key}`,
                              )}
                            </span>
                            <span className="w-40 shrink-0 text-right font-extrabold tabular-nums">
                              {row.tons}
                            </span>
                            <span className="w-44 shrink-0 text-right font-bold tabular-nums text-chart-indigo">
                              {row.milkUsage}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </section>
  );
};
